import { AppContext } from "App";

import { HeadersBuilder } from "Global/Base/Comunication/HeadersBuilder";
import { ErrorHandler } from "UI/Errors";
import { HTTPRequest, IHTTPResult } from "../../Global/Base/Comunication/HTTPRequest";
import { HTTPStatus } from "../../Global/Base/Comunication/HTTPStatus";
import { InjectedProperty } from "../../Global/Base/DIContainer";
import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { GlobalContext } from "../../Global/Base/GlobalContext";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { DateUtils } from "../../Global/Base/Utils/DateUtils";
import { StringUtils } from "../../Global/Base/Utils/StringUtils";
import { createElement } from "../../UI/UI.ElementarControls/Utils/CustomElementUtils";
import { LocalizedStringBuilder } from "../../UI/UI/UI/DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../UI/UI/UI/MessageWindow";
import { UIManager } from "../../UI/UI/UI/UIManager";
import { ApplicationStorage, AppStorageItemType } from "./ApplicationStorage";
import { HEADER_ROLES_NAME, HEADER_TRANSACTION_CONTEXT_ID_NAME } from "./Constants";
import { ITableExportParams } from "./OWManagerInterfaces";
import { EMPTY_REQUEST_CONTEXT, RequestContext } from "./RequestContext";

/**
 * Podporované typy tisku
 */
export enum SupportedReportFormats {
    PDF,
    Word,
    Excel,
}

/**
 * Podporované typy exportu
 */
export enum SupportedExportFormats {
    CSV,
    Excel2007,
}

const API_REPORT_URL_PART = "api/v01/as/reports";

export class ExportManagerWorker implements IExportManagerWorker {
    /**
     * Interval čekání mezi jednotlivými dotazy na zjištění výsledku tisku
     */
    private readonly _backoffInterval = 3000;
    private readonly _webServiceUrl: string;
    private readonly _useLeagecyApi: boolean;

    /**
     * @param webServiceUrl Url webové služby, která provádí exporty
     * @param useLeagecyApi Zda se má komunikovat se starým rozhraním služby
     */
    constructor(webServiceUrl: string, useLeagecyApi: boolean) {
        this._webServiceUrl = webServiceUrl;
        this._useLeagecyApi = useLeagecyApi;
    }

    protected _createApiUrl(handlerUrlPart: string, parameters?: string): string {
        return `${this._webServiceUrl}/${handlerUrlPart}${parameters ? `?${parameters}` : ""}`;
    }

    /**
     * Tisk sestavy, spuštěný z komponenty ReportAction
     * @param instanceID Instance master objektu
     * @param reportName Název reportu, ze kterého bude vytvářen výstup
     * @param reportLanguage Použitý jazyk (cs, en, ...)
     * @param outputType pdf, docx, ...
     * @param readVersioned Zda bude použit verzovaný záznam
     * @param requestContext Viz {@link RequestContext}
     */
    public printReport(instanceID: string, reportName: string, reportLanguage: string, outputType: string, readVersioned?: boolean, requestContext?: RequestContext): Promise<void> {
        let urlParameters = `iid=${instanceID}&file=${encodeURIComponent(reportName)}`;

        if (reportLanguage) {
            urlParameters += `&languagename=${reportLanguage}`;
        } else if (ApplicationStorage.get(AppStorageItemType.reportLanguage)) {
            urlParameters += `&languagename=${ApplicationStorage.get(AppStorageItemType.reportLanguage)}`;
        }

        if (outputType)
            urlParameters += `&output=${outputType}`;

        if (readVersioned)
            urlParameters += `&readVersioned=${readVersioned}`;

        urlParameters = ExportManagerWorker._appendRequestContextParam(urlParameters, requestContext, true);

        const url = this._createApiUrl(this._useLeagecyApi ? "handlers/exports/report.aspx" : `${API_REPORT_URL_PART}/report`, urlParameters);
        return this._sendPrintRequest(url, StringUtils.compareCaseInsensitive(outputType, SupportedReportFormats[SupportedReportFormats.PDF]), undefined, requestContext);
    }

    /**
     * Tisk jednoho konkrétního záznamu
     * @param id Id záznamu
     * @param frameName Název framu
     * @param formName Název formu, na kterém záznam leží
     * @param readVersioned Zda bude použit verzovaný záznam
     * @param requestContext Viz {@link RequestContext}
     */
    public printRecord(id: string, className: string, frameName: string, formName: string, readVersioned?: boolean, requestContext?: RequestContext): Promise<void> {
        const reportType = ApplicationStorage.get(AppStorageItemType.reportType);
        let urlParameters = `detailObjects=${frameName}:${id}&objectName=${className}&formName=${encodeURIComponent(formName)}&readVersioned=${readVersioned}&mode=1&reportType=${reportType}`;

        urlParameters = ExportManagerWorker._appendRequestContextParam(urlParameters, requestContext, true);

        const url = this._createApiUrl(this._useLeagecyApi ? "handlers/exports/detailReport.aspx" : `${API_REPORT_URL_PART}/detail-report`, urlParameters);

        return this._sendPrintRequest(url, StringUtils.compareCaseInsensitive(reportType, SupportedReportFormats[SupportedReportFormats.PDF]), undefined, requestContext);
    }

    /**
     * Tisk seznamu
     * @param params Data nutná pro spuštění tisku
     * @param requestContext Viz {@link RequestContext}
     */
    // Pro předání dat na server je vytvořen dočasný formulář s inputem "data", který je po odeslání smazán
    public printFrame(params: ITableExportParams, requestContext?: RequestContext): Promise<void> {
        const urlParameters = ExportManagerWorker._appendRequestContextParam("", requestContext, false);

        const url = this._createApiUrl(this._useLeagecyApi ? "handlers/exports/frameReport.aspx" : `${API_REPORT_URL_PART}/frame-report`, urlParameters);

        return this._sendPrintRequest(url, StringUtils.compareCaseInsensitive(params.exportType, SupportedReportFormats[SupportedReportFormats.PDF]), params, requestContext);
    }

    /**
     * Pošle dotaz na vytvoření sestavy, počká na její vytvoření a zobrazí ji nebo nabídne ke stažení
     * @param printUrl Adresa dotazu pro zahájení tisku
     * @param openInNewWindow Zda se má výsledek otevřít v novém okně
     * @param params Volitelné parametry dotazu na zahájení tisku
     * @param requestContext Viz {@link RequestContext}
     */
    protected async _sendPrintRequest(printUrl: string, openInNewWindow: boolean, params?: object, requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<void> {
        try {
            UIManager.instance.showLoader();
            const { cancellationToken } = requestContext;
            let request: IHTTPResult = null;
            try {
                request = await HTTPRequest.universalRequest({
                    requestMethod: params != null ? "POST" : "GET",
                    url: printUrl,
                    data: JSON.stringify(params),
                    readResponseInfos: true,
                    okStatusSet: [HTTPStatus.ok, HTTPStatus.preconditionFailed],
                    headers: ExportManagerWorker._createHeaders(requestContext),
                }, cancellationToken);
            } finally {
                UIManager.instance.hideLoader();
            }

            if (request == null)
                throw new InvalidOperationException(`Request to url '${printUrl}' failed but didn't end with an exception`); //

            if (request.status === HTTPStatus.preconditionFailed) {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-665842"), "GUI-665841"); // Tisk nelze dokončit; Požadovaná sestava obsahuje příliš mnoho sloupců
                return;
            }

            const response: IPrintInfoDTO | null = request.response ? JSON.parse(request.response) as IPrintInfoDTO : null;

            if (!response || response.reportInDispatcher == null)
                throw new InvalidOperationException(`Received invalid response from url '${printUrl}'`); //`

            let fileUrl: string;

            if (response.reportInDispatcher) {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-665801"), "GUI-665800"); // Tisk se připravuje; Po dokončení bude sestava automaticky stažena

                // u tisku na dispatcheru se čeká pomocí handleru taskresult na dokončení úkolu
                let urlParameters = `taskId=${response.taskID}`;
                urlParameters = ExportManagerWorker._appendRequestContextParam(urlParameters, requestContext, true);
                fileUrl = this._createApiUrl(this._useLeagecyApi ? "handlers/exports/taskresult.aspx" : "api/v01/as/data-trans/task-result", urlParameters);
                params = undefined;

                if (!(await this._waitForPrintDone(fileUrl, requestContext)))
                    return;
            } else {
                // jinak se tiskne přímo na aplikáči
                fileUrl = printUrl + (printUrl.indexOf("?") !== -1 ? "&" : "?") + "printReport=true"; // zařídí, že se tisk začne opravdu provádět
            }

            if (openInNewWindow) {
                this._openWindow(fileUrl, params);
            } else {
                this._downloadResponse(fileUrl, params);
            }
        } catch (e) {
            ErrorHandler.instance.handleError(e);
        }
    }

    protected _openWindow(url: string, params?: object): void {
        if (params == null) {
            window.open(url, undefined, "toolbar=no,menubar=no,resizable=yes");
            return;
        }

        // pokud je potřeba posílat dodatečné parametry, tak se musí výsledek do okna posílat přes form, který odešle potřebná dodatečná data
        const target = Date.now().toString(36); // Aby se nepřepisoval obsah již otevřených oken
        const form = this._createForm(url, params);
        form.target = target;
        document.body.appendChild(form);

        window.open("", target, "toolbar=no,menubar=no,resizable=yes"); // Pokud by se otevíralo přímo na formu přes "_blank" nebo atribut action, tak se otevře v tabu a ne v okně
        (form as HTMLFormElement).submit();
        document.body.removeChild(form);
    }

    protected _downloadResponse(url: string, params?: object): void {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        document.body.appendChild(iframe);

        if (params == null) {
            iframe.src = url;
            return;
        }

        // pokud je potřeba posílat dodatečné parametry, tak se musí dotaz udělat přes form, který odešle potřebná dodatečná data
        const form = this._createForm(url, params);

        iframe.contentDocument.body.appendChild(form);

        (form as HTMLFormElement).submit();
    }

    /**
     * Vytvoří form element s jedním inputem
     * @param url Adresa pro odeslání formuláře
     * @param params Parametry předané do inputu
     */
    protected _createForm(url: string, params?: object): HTMLFormElement {
        const form = createElement({ type: "form", attrs: { "action": url, "method": "POST" }, css: { display: "none" } }) as HTMLFormElement;

        const input = createElement({ type: "input", attrs: { "type": "hidden", "name": "data" } }) as HTMLInputElement;
        input.value = JSON.stringify(params);

        form.appendChild(input);

        return form;
    }

    /**
     * Čeká na dokončení tisku na dispatcheru
     * @param url Adresa pro dotazování
     * @param ct Umožňuje stornování čekání
     */
    protected async _waitForPrintDone(url: string, requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<boolean> {
        try {
            for (;;) {
                // nejdříve se udělá preflight HEAD dotaz na stav tisku
                const response = await HTTPRequest.universalRequest({
                    requestMethod: "HEAD",
                    url: url,
                    readResponseInfos: true,
                    okStatusSet: [HTTPStatus.ok, HTTPStatus.accepted],
                    headers: ExportManagerWorker._createHeaders(requestContext),

                }, requestContext.cancellationToken);

                if (response.status === HTTPStatus.accepted) {
                    await PromiseUtils.delay(this._backoffInterval, requestContext.cancellationToken);
                    continue;
                }

                return true;
            }
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return false;
        }
    }

    private static _createHeaders(requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): HeadersBuilder {
        const roles = AppContext.instance.configuration.legacyActiveRoles ? AppContext.instance.activeUserRolesIds : requestContext.roles;
        const getTransactionContextId = requestContext.getTransactionContextId;
        const transationContextId = getTransactionContextId ? getTransactionContextId() : undefined;
        if (roles?.length || transationContextId) {
            const headers = new HeadersBuilder(HTTPRequest.jsonHeaders);
            if (roles?.length) {
                headers.set(HEADER_ROLES_NAME, roles.join(";"));
            }
            if (transationContextId) {
                headers.set(HEADER_TRANSACTION_CONTEXT_ID_NAME, transationContextId);
            }
            return headers;
        }

        return undefined;
    }

    /**
     * Export seznamu
     * @param params Data nutná pro spuštění exportu
     * @param ct Umožňuje stornování
     * @param requestContext Viz {@link RequestContext}
     */
    public async exportFrame(params: ITableExportParams, requestContext?: RequestContext): Promise<void> {
        let response: IHTTPResult = null;

        UIManager.instance.showLoader();

        const url = this._useLeagecyApi ? "handlers/exports/export.aspx" : "api/v01/as/exports/export";
        const urlParameters = ExportManagerWorker._appendRequestContextParam("", requestContext, false);

        try {
            response = await HTTPRequest.universalRequest({
                requestMethod: "POST",
                url: this._createApiUrl(url, urlParameters),
                readResponseInfos: true,
                data: JSON.stringify(params),
                responseType: "blob",
                headers: ExportManagerWorker._createHeaders(requestContext),
            }, requestContext?.cancellationToken);
        } catch (exception) {
            ErrorHandler.instance.handleError(exception);
        } finally {
            UIManager.instance.hideLoader();
        }

        if (response == null) {
            return;
        }

        const contentHeader = response.getResponseHeader("Content-Disposition");
        let fileName = DateUtils.getSpecificDate(0); // Default, když nic nepřijde v hlavičce "Content-Disposition"

        if (contentHeader) {
            const headerParts = contentHeader.split("filename=");

            if (headerParts.length === 2) {
                fileName = headerParts[1].substr(1).slice(0, -1); // Nalezený název souboru bez uvozovek
            }
        }

        const link = createElement({ type: "a", attrs: { "href": window.URL.createObjectURL(new File([response.response], fileName, { type: response.contentType })), "download": fileName } }) as HTMLLinkElement;

        top.document.body.appendChild(link); // Pokud by byl vložený do iframe, tak nebude fungovat ve FF bez přidání "blob:" do CSP (testováno ve verzi 61)

        link.click();

        top.document.body.removeChild(link);
    }

    private static _appendRequestContextParam(paramUrl: string, requestContext: RequestContext, withAmpersand: boolean): string {
        if (requestContext?.getTransactionContextId()) {
            paramUrl += `${withAmpersand ? "&" : ""}ContextID=${requestContext.getTransactionContextId()}`;
        }
        return paramUrl;
    }
}

/**
 * @includeToDoc
 */
export interface IExportManagerWorker {
    printReport(instanceID: string, reportName: string, reportLanguage: string, outputType: string, readVersioned?: boolean, requestContext?: RequestContext): Promise<void>;
    printRecord(id: string, className: string, frameName: string, formName: string, readVersioned?: boolean, requestContext?: RequestContext): void;
    printFrame(params: ITableExportParams, requestContext?: RequestContext): void;
    exportFrame(params: ITableExportParams, requestContext?: RequestContext): Promise<void>;
}

export class IExportManagerWorker {
    private static _properties = ["printReport", "printRecord", "printFrame", "exportFrame"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !IExportManagerWorker._properties.some((value) => !Reflect.has(instance, value));
    }
}

export class ExportManager {
    private static get _instance(): IExportManagerWorker {
        return {} as any;
    }

    /**
     * Instance v kontextu současného okna
     */
    public static get instance(): IExportManagerWorker {
        return ExportManager._instance;
    }
}

InjectedProperty.bindProperty(ExportManager, "_instance", IExportManagerWorker);
GlobalContext.registerType(ExportManager);

interface IPrintInfoDTO {
    reportInDispatcher: boolean;
    taskID: string | null;
}
